import React from 'react';
import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { previewSupportMessage } from '../utils/supportPhotos';

const ZOOM_STEPS = [1, 1.5, 2, 3, 4];

interface SupportPhotoGridProps {
    urls: string[]
    /** Thumbnail edge in px. */
    size?: number
}

/** The photos of a support message as thumbnails; a click opens them in [SupportPhotoViewer]. */
export function SupportPhotoGrid({ urls, size = 120 }: SupportPhotoGridProps) {
    const [open, setOpen] = React.useState<number | null>(null);
    if (urls.length === 0) return null;
    return (
        <>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {urls.map((url, i) => (
                    <Box
                        key={url + i}
                        component="img"
                        src={url}
                        alt={`Photo ${i + 1}`}
                        loading="lazy"
                        onClick={() => setOpen(i)}
                        data-testid="support-photo-thumb"
                        sx={{
                            width: size,
                            height: size,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid rgba(0,0,0,0.15)',
                            cursor: 'zoom-in',
                            backgroundColor: '#eee',
                        }}
                    />
                ))}
            </Stack>
            <SupportPhotoViewer urls={urls} index={open} onIndex={setOpen} />
        </>
    );
}

interface SupportPhotoViewerProps {
    urls: string[]
    /** The photo shown; null = closed. */
    index: number | null
    onIndex: (index: number | null) => void
}

/**
 * One photo over the page at a fitting size: zoom with the buttons, the wheel or a click (cycles 1× → 4×), drag the
 * scroll bars when zoomed, arrows (and ← →) between the photos of the message, the original in a new tab.
 */
export function SupportPhotoViewer({ urls, index, onIndex }: SupportPhotoViewerProps) {
    const [zoom, setZoom] = React.useState(0);
    React.useEffect(() => setZoom(0), [index]);

    const count = urls.length;
    const go = React.useCallback((step: number) => {
        if (index == null || count < 2) return;
        onIndex((index + step + count) % count);
    }, [index, count, onIndex]);

    React.useEffect(() => {
        if (index == null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') go(-1);
            if (e.key === 'ArrowRight') go(1);
            if (e.key === '+' || e.key === '=') setZoom(z => Math.min(ZOOM_STEPS.length - 1, z + 1));
            if (e.key === '-') setZoom(z => Math.max(0, z - 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [index, go]);

    if (index == null || index >= count) return null;
    const url = urls[index];
    const scale = ZOOM_STEPS[zoom];

    return (
        <Dialog open fullWidth maxWidth="lg" onClose={() => onIndex(null)}
            PaperProps={{ sx: { backgroundColor: '#111', color: '#fff', height: '90vh' } }}>
            <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1, pl: 1 }}>
                    {count > 1 ? `${index + 1} / ${count}` : 'Photo'} · {zoom === 0 ? 'Fit' : `${scale}×`}
                </Typography>
                <Tooltip title="Zoom out (−)">
                    <span>
                        <IconButton color="inherit" disabled={zoom === 0} onClick={() => setZoom(z => Math.max(0, z - 1))}>
                            <ZoomOutIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Zoom in (+)">
                    <span>
                        <IconButton color="inherit" disabled={zoom === ZOOM_STEPS.length - 1}
                            onClick={() => setZoom(z => Math.min(ZOOM_STEPS.length - 1, z + 1))}>
                            <ZoomInIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Open the original in a new tab">
                    <IconButton color="inherit" component="a" href={url} target="_blank" rel="noopener noreferrer">
                        <OpenInNewIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                    <IconButton color="inherit" onClick={() => onIndex(null)}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <Box
                    onWheel={e => {
                        if (!e.ctrlKey && !e.metaKey) return;
                        setZoom(z => Math.max(0, Math.min(ZOOM_STEPS.length - 1, z + (e.deltaY < 0 ? 1 : -1))));
                    }}
                    sx={{ position: 'absolute', inset: 0, overflow: 'auto', display: 'flex' }}>
                    <Box
                        component="img"
                        src={url}
                        alt={`Photo ${index + 1}`}
                        data-testid="support-photo-full"
                        onClick={() => setZoom(z => (z + 1) % ZOOM_STEPS.length)}
                        sx={zoom === 0
                            ? { m: 'auto', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: 'zoom-in' }
                            : { m: 'auto', width: `${scale * 100}%`, maxWidth: 'none', cursor: zoom === ZOOM_STEPS.length - 1 ? 'zoom-out' : 'zoom-in' }}
                    />
                </Box>
                {count > 1 && (
                    <>
                        <IconButton color="inherit" onClick={() => go(-1)}
                            sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.4)' }}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton color="inherit" onClick={() => go(1)}
                            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.4)' }}>
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}
            </Box>
        </Dialog>
    );
}

/** A tooltip-friendly sentence for a preview: "My balance is wrong · 2 photos". */
export const describeSupportMessage = (text: string | null | undefined): string => {
    const { words, photos, shared } = previewSupportMessage(text);
    const parts = [
        words,
        photos > 0 ? (photos === 1 ? '1 photo' : `${photos} photos`) : '',
        ...shared.map(type => `shared ${type}`),
    ].filter(part => part !== '');
    return parts.join(' · ');
}

/**
 * The last message of a chat in a list cell: photo blocks as a photo icon (×N), other shared blocks as a share icon
 * with their type, then the words — never the blocks' JSON.
 */
export function SupportMessagePreviewText({ text, empty = 'No message', lines = 2 }: { text: string | null | undefined; empty?: string; lines?: number }) {
    const { words, photos, shared } = previewSupportMessage(text);
    if (words === '' && photos === 0 && shared.length === 0) {
        return <Typography variant="caption" color="text.secondary">{empty}</Typography>;
    }
    return (
        <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            data-testid="support-message-preview"
            sx={{
                display: '-webkit-box',
                WebkitLineClamp: lines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                whiteSpace: 'normal',
                lineHeight: 1.4,
            }}>
            {photos > 0 && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mr: 0.75, verticalAlign: 'middle', color: 'primary.main' }}>
                    <PhotoOutlinedIcon sx={{ fontSize: 16 }} titleAccess={photos === 1 ? 'Photo' : `${photos} photos`} />
                    {photos > 1 && <span>×{photos}</span>}
                </Box>
            )}
            {shared.map((type, i) => (
                <Box key={type + i} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mr: 0.75, verticalAlign: 'middle' }}>
                    <ShareOutlinedIcon sx={{ fontSize: 14 }} titleAccess={`Shared ${type}`} />
                    <span>{type}</span>
                </Box>
            ))}
            {words}
        </Typography>
    );
}
